import React from 'react';

import moment from 'moment-timezone';

import styled from 'styled-components';
import Button from '@grnhse/seedling/lib/azalea/components/button';

import { padding, colors } from '@grnhse/seedling/lib/azalea/constants';
import { withLocalePrefix } from 'shared/utils/translation';

import TimeSlotInput from 'shared/components/dates/TimeSlotInput';
import type { TimeSlotValue } from 'shared/components/dates/TimeSlotInput';
import type { SuggestedTimeSlot } from './suggested_times';

// @ts-expect-error - TS7016 - Could not find a declaration file for module './styles'. 'app/webpack/javascripts/candidate_availability/styles.js' implicitly has an 'any' type.
import { AvailabilityContainer } from './styles';

const t = withLocalePrefix('availability.choose_availability');

type CandidateTimeSlotState = {
  // the original suggested time that gave birth to a candidate time
  originalSuggestedTime?: SuggestedTimeSlot;
};

export type CandidateTimeSlot = TimeSlotValue & CandidateTimeSlotState;

type Props = {
  candidateTimes: Array<CandidateTimeSlot>;
  step: number;
  onChange: (
    timeSlots: Array<CandidateTimeSlot>,
    removedTimeRange?: SuggestedTimeSlot
  ) => unknown;
};

/**
 * Encapsulates the logic for adding/removing/editing candidate times
 * */
export default class CandidateTimes extends React.PureComponent<Props> {
  constructor(props: Props) {
    super(props);
  }

  /**
   * The start/end time should fall exactly on a step.
   *
   * In other words, if the time can only be selected in increments of 15min, the start/end
   * time can't be something like 5:35pm. It can only be 5:45pm.
   *
   * This finds the next available increment and returns that.
   * */
  getNewAvailableTimeRange(): CandidateTimeSlot {
    const { step } = this.props;

    const now = moment();
    let stepTime = moment(now).startOf('hour');

    while (stepTime.isBefore(now)) {
      stepTime = moment(stepTime).add(step, 'ms');
    }

    return {
      start: stepTime,
      end: moment(stepTime).add(step, 'ms'),
    };
  }

  addNewAvailability = (): void => {
    const { candidateTimes, onChange } = this.props;
    onChange([...candidateTimes, this.getNewAvailableTimeRange()]);
  };

  onTimeRangeRemoved = (timeRange: CandidateTimeSlot): void => {
    const { candidateTimes, onChange } = this.props;

    const newCandidateTimes = candidateTimes.filter(
      (time) => time !== timeRange
    );

    onChange(newCandidateTimes, timeRange.originalSuggestedTime);
  };

  onTimeRangeChanged = (newTimeRange: CandidateTimeSlot, idx: number): void => {
    const { candidateTimes, onChange } = this.props;

    let newAvailabilities = [...candidateTimes];
    newAvailabilities[idx] = newTimeRange;

    onChange(newAvailabilities);
  };

  renderCandidateTime(timeRange: CandidateTimeSlot, idx: number) {
    const { step } = this.props;

    const onChange = (changedTimeRange: TimeSlotValue) => {
      // the object returned is a TimeSlotValue, not a CandidateTimeSlot
      // be sure to merge all the other properties into this object before returning.
      const changedTimeSlot = Object.assign({}, timeRange, changedTimeRange);
      this.onTimeRangeChanged(changedTimeSlot, idx);
    };

    const onRemove = () => {
      this.onTimeRangeRemoved(timeRange);
    };

    return (
      <AvailabilityContainer key={idx}>
        <TimeSlotInput
          min={moment()}
          max={moment().add(3, 'months')}
          // @ts-expect-error - TS2769 - No overload matches this call.
          className="mobile-input-text"
          step={step}
          value={timeRange}
          onChange={onChange}
        />

        <RemoveTimeLinkContainer>
          <RemoveTimeLink className="remove-time" onClick={onRemove}>
            {t('actions.remove_availability')}
          </RemoveTimeLink>
        </RemoveTimeLinkContainer>
      </AvailabilityContainer>
    );
  }

  render() {
    const { candidateTimes } = this.props;

    return (
      <div>
        {candidateTimes.map((timeRange, idx) =>
          this.renderCandidateTime(timeRange, idx)
        )}

        <Button
          size="large"
          className="add-availability"
          onClick={this.addNewAvailability}
          block
        >
          {t('actions.add_new_availability')}
        </Button>
      </div>
    );
  }
}

const RemoveTimeLinkContainer = styled.div`
  text-align: right;
`;

const RemoveTimeLink = styled.a`
  display: inline-block;
  margin-top: ${padding.small};
  margin-bottom: ${padding.normal};

  text-decoration: none;
  color: ${colors.mediumBlue};
  cursor: pointer;
`;
